// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type { NudgeRequest, NudgeResult } from '../types/nudge';

/**
 * CoA advisor-triggered nudge client.
 *
 * <p>Mirror of {@link nudgeApi.sendNudge} targeting the CoA surface:
 * the advisor's paragraph is injected into the shared
 * {@code emails/coa-provider-notification.html} template's
 * {@code customBody} slot at send time (server-side), following the
 * same unified pattern used by LOA nudges and CoA/LOA client
 * invitations.
 */
export const sendCoaNudge = async (
  coaId: string,
  policyId: string,
  request: NudgeRequest,
): Promise<NudgeResult> => {
  // Route via the unified Profile Request surface
  // ({@code /api/requests/{requestId}/policies/{policyId}/nudge}). The
  // CoA's `requestId` comes from the CoADTO returned by GET /api/coa/{id}
  // (added in Phase B PR 2a).
  const coa = await api.get<{ requestId?: string }>(`/coa/${coaId}`);
  const requestId = coa.data.requestId;
  if (!requestId) {
    throw new Error(
      'CoA has no linked Profile Request — Profile Requests must be enabled for this tenant.',
    );
  }
  const response = await api.post<NudgeResult>(
    `/requests/${requestId}/policies/${policyId}/nudge`,
    request,
  );
  return response.data;
};

export default { sendCoaNudge };
